import React, { ReactNode, useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { CSSTransition } from 'react-transition-group';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setCreateQuizActive, setIsEditingQuiz } from '@/lib/slices/quiz-ui-slice';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { resetActiveCourseData } from '@/lib/slices/course-slice';
import { resetQuizData } from '@/lib/slices/quiz-slice';
import { resetActiveGroupData } from '@/lib/slices/admin-group-slice';

import AdminCourseStudentViewManager from '@/(portal)/admin/(pages)/courses/components/course-management/student-view/AdminCourseStudentViewManager';
import CourseOverview from '@/common/components/course/CourseOverview';
import QuizBuilderContainer from '@/(portal)/admin/(pages)/courses/components/quiz-setup/QuizBuilderContainer';
import QuizListView from '@/(portal)/admin/(pages)/courses/components/quiz-setup/QuizListView';
import CourseCopyWizard from '@/common/components/admin/modals/CourseCopyWizard';
import ContentCreatorWizard from '@/common/components/admin/modals/ContentCreatorWizard';
import SubunitCreatorWizard from '@/common/components/admin/modals/SubunitCreatorWizard';
import UnitCreatorWizard from '@/common/components/admin/modals/UnitCreatorWizard';
import DeleteCourseModal from '@/common/components/modals/custom/confirmation/DeleteCourseModal';
import DeleteUnitModal from '@/common/components/modals/custom/confirmation/DeleteUnitModal';
import DeleteSubunitModal from '@/common/components/modals/custom/confirmation/DeleteSubunitModal';
import DeleteContentModal from '@/common/components/modals/custom/confirmation/DeleteContentModal';
import AddCourseAdminModal from '@/common/components/modals/custom/management/AddCourseAdminModal';
import Button from '@/common/components/ui/Button';
import { ChevronLeftIcon } from '@/assets/icons/chevron';
import { DeleteItemIcon, AddIcon, CopyIcon } from '@/assets/icons/button';

import { CourseData } from 'course-types';
import { role } from 'auth-types';
import AdminCourseAccessService from '@/services/course/admin/admin-course-access-service';

export default function SelectedAdminCourseView(): React.JSX.Element {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );
  const adminStudentView: boolean = useAppSelector(() => store.getState().course.adminStudentView);
  const createQuizActive: boolean = useAppSelector(() => store.getState().quizUi.createQuizActive);
  const isEditingQuiz: boolean = useAppSelector(() => store.getState().quizUi.isEditingQuiz);
  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);

  // Reactive selector so the button label and back destination are always in sync
  const activeGroupData = useAppSelector(() => store.getState().adminGroups?.activeGroupData);

  const [viewType, setViewType] = useState<'course' | 'quiz'>('course');
  const [showSlide, setShowSlide] = useState(false);
  const [transitionPanelEntered, setTransitionPanelEntered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    if (adminStudentView) {
      setShowSlide(true);
    } else {
      setTransitionPanelEntered(false);
    }
  }, [adminStudentView]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddQuiz = (): void => {
    dispatch(setCreateQuizActive(true));
    dispatch(setIsEditingQuiz(false));
    dispatch(resetQuizData());
  };

  const handleEditQuiz = (): void => {
    dispatch(setCreateQuizActive(true));
    dispatch(setIsEditingQuiz(true));
  };

  const finishCreate = (): void => {
    dispatch(setCreateQuizActive(false));
    dispatch(setIsEditingQuiz(false));
  };

  useEffect(() => {
    setIsEditable(AdminCourseAccessService.getEditableAccess(userRole, activeCourseData));
  }, [activeCourseData, userRole]);

  const handleBackClick = () => {
    if (activeGroupData?.id) {
      // Came from a group
      router.push('/admin/group');
    } else {
      // Came from All Courses
      dispatch(resetActiveCourseData());
      dispatch(resetActiveGroupData());
      router.push('/admin/courses');
    }
  };

  const renderView = (): ReactNode => {
    if (createQuizActive) {
      return (
        <QuizBuilderContainer
          visible={createQuizActive}
          cancelCreate={finishCreate}
          isEditing={isEditingQuiz}
        />
      );
    }

    const props = {
      handleAddQuiz: handleAddQuiz,
      handleEditQuiz: handleEditQuiz,
    };

    if (viewType === 'course') {
      return (
        <div className='relative h-full'>
          {/* Course Overview - Admin View */}
          <CourseOverview
            id={activeCourseData.id}
            courseName={activeCourseData.name}
            isAdminView={true}
            isEditable={isEditable}
          />
        </div>
      );
    }
    return <QuizListView {...props} />;
  };

  return (
    <div className='relative w-full h-full'>
      {/* Header with action buttons */}
      {!adminStudentView && !createQuizActive && (
        <div>
          <div className='flex items-center justify-between px-6 py-4'>
            <Button
              text={true}
              onClick={handleBackClick}
              className='flex self-start items-center gap-2 px-3 py-2 text-primary hover:bg-surface_gray_50 transition-colors'
            >
              <ChevronLeftIcon className='w-4 h-4' />
              <span className='text-xs text-primary font-semibold'>
                {activeGroupData?.id ? 'Back to group' : 'Back to courses'}
              </span>
            </Button>

            <div className='flex items-center gap-2'>
              {isEditable && (
                <Button
                  onClick={() => {
                    dispatch(setModalVisible(true));
                    dispatch(setModalName('AddCollaborator'));
                  }}
                  className='flex w-full h-auto items-center gap-2 px-3 py-2 rounded-lg border shadow-sm'
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #EFF8FF',
                    background: '#EFF8FF',
                    boxShadow: '0 1px 2px 0 rgba(10, 13, 18, 0.05)',
                  }}
                >
                  <AddIcon
                    className='w-4 h-4'
                    style={{
                      color: '#1570EF',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '20px',
                    }}
                  />
                  <span
                    style={{
                      color: '#1570EF',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: '600',
                      lineHeight: '20px',
                    }}
                  >
                    Add collaborator
                  </span>
                </Button>
              )}

              {/* Three-dot menu */}
              {isEditable && (
                <div className='relative' ref={dropdownRef}>
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className='flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors'
                  >
                    <svg className='w-5 h-5' viewBox='0 0 16 16' fill='none'>
                      <circle cx='8' cy='8' r='1.5' fill='#A4A7AE' />
                      <circle cx='8' cy='3' r='1.5' fill='#A4A7AE' />
                      <circle cx='8' cy='13' r='1.5' fill='#A4A7AE' />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {showDropdown && (
                    <div className='absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50'>
                      <button
                        onClick={() => {
                          dispatch(setModalVisible(true));
                          dispatch(setModalName('CopyCourse'));
                          setShowDropdown(false);
                        }}
                        className='w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-3'
                      >
                        <CopyIcon className='h-4 w-4 flex-shrink-0' />
                        Copy Course
                      </button>
                      <button
                        onClick={() => {
                          dispatch(setModalVisible(true));
                          dispatch(setModalName('DeleteCourse'));
                          setShowDropdown(false);
                        }}
                        className='w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3'
                      >
                        <DeleteItemIcon className='h-4 w-4 flex-shrink-0' />
                        Delete Course
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {renderView()}
        </div>
      )}

      {/* Render quiz editor when active */}
      {createQuizActive && renderView()}

      {/* Global overlay transitions that cover header + body */}
      {viewType === 'course' && (
        <>
          {/* Sliding Transition Panel (covers header and body) */}
          <CSSTransition
            in={showSlide}
            timeout={300}
            classNames='slide'
            unmountOnExit
            onEntered={() => setTransitionPanelEntered(true)}
          >
            <div className='absolute inset-0 bg-background_gray_50 z-10'></div>
          </CSSTransition>

          {/* AdminCourseStudentViewManager with fade-in on top of slide */}
          <CSSTransition
            in={transitionPanelEntered}
            timeout={300}
            classNames='fade'
            unmountOnExit
            onExited={() => setShowSlide(false)}
          >
            <div className='absolute inset-0 z-20'>
              <AdminCourseStudentViewManager />
            </div>
          </CSSTransition>
        </>
      )}

      {/* Modals */}
      <CourseCopyWizard />
      <ContentCreatorWizard />
      <SubunitCreatorWizard />
      <UnitCreatorWizard />
      <DeleteCourseModal />
      <DeleteUnitModal />
      <DeleteSubunitModal />
      <DeleteContentModal />
      <AddCourseAdminModal />
    </div>
  );
}
